import React, { FC, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import axios, { AxiosError } from 'axios';
import Button from '../../../components/Button/Button';
import useSnackbarNotifications from '../../../hooks/useSnackbarNotifications';
import {
  BULK_UPDATE_ENTITY_RULES,
  BULK_UPDATE_REQUIRED_COLUMNS,
  BULK_UPDATE_VALUE_REQUIRED_NOTE,
  TBulkUpdateEntity,
} from '../../../utils/bulkUpdateValidation';
import { downloadBulkUpdateTemplate } from '../../../utils/bulkUpdateTemplate';
import BulkUpdateDropzone from './BulkUpdateDropzone';
import BulkUpdateErrorTable from './BulkUpdateErrorTable';
import { useBulkUpdateUpload } from './useBulkUpdateUpload';

type Props = {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
};

const getUploadErrorMessage = (error: unknown): string => {
  const axiosError = error as AxiosError<{ message?: string }>;
  if (axiosError?.code === 'ECONNABORTED') {
    return 'The upload timed out. The file may still be processing, so check the list before uploading again.';
  }
  if (axiosError?.isAxiosError && !axiosError.response) {
    return 'The server could not be reached. Check your connection and try again.';
  }
  const httpStatus = axiosError?.response?.status;
  if (httpStatus === 413) {
    return 'The server rejected the file because it is too large.';
  }
  const serverMessage = axiosError?.response?.data?.message;
  if (serverMessage) {
    return serverMessage;
  }
  return error instanceof Error ? error.message : 'The file could not be uploaded. Please try again.';
};

const COUNTRY_ENTITY_RULES = BULK_UPDATE_ENTITY_RULES.filter((rule) => rule.allowedCountries);

const renderValueNote = () => (
  <Alert severity="info">
    <Stack spacing={0.5}>
      {COUNTRY_ENTITY_RULES.map((rule) => (
        <Typography key={rule.entity} variant="caption" display="block">
          <strong>{rule.entity}</strong> - use these country codes in the Value column:{' '}
          {rule.allowedCountries?.map((country) => `${country.value} (${country.label})`).join(', ')}.
        </Typography>
      ))}
      <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
        {BULK_UPDATE_VALUE_REQUIRED_NOTE}
      </Typography>
    </Stack>
  </Alert>
);

const BulkUpdateModal: FC<Props> = ({ open, onClose, onCompleted }) => {
  const { status, file, rows, errors, fatalError, result, selectFile, submit, reset } = useBulkUpdateUpload();
  const { showErrorSnackbar } = useSnackbarNotifications();
  const [isDownloading, setIsDownloading] = useState(false);

  const isBusy = status === 'validating' || status === 'submitting';

  const handleClose = (reason?: string) => {
    // Clicking away must not silently abort an in-flight upload or validation.
    if (isBusy && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    if (status === 'validating') {
      return;
    }
    reset();
    onClose();
  };

  const handleDownloadTemplate = async (entity: TBulkUpdateEntity) => {
    setIsDownloading(true);
    try {
      await downloadBulkUpdateTemplate(entity);
    } catch (error) {
      showErrorSnackbar({ message: 'The template could not be downloaded. Please try again.' });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await submit();
      // The in-modal result is the only success notification, so no snackbar is raised here.
      onCompleted?.();
    } catch (error) {
      if (axios.isCancel(error) || (error as Error)?.name === 'CanceledError') {
        return;
      }
      showErrorSnackbar({ message: getUploadErrorMessage(error) });
    }
  };

  const renderTemplates = () => (
    <Stack spacing={1}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Download a sample template
      </Typography>
      {BULK_UPDATE_ENTITY_RULES.map((rule) => (
        <Stack key={rule.entity} direction="row" alignItems="center" gap={1}>
          <Button
            size="small"
            disabled={isDownloading}
            startIcon={<Icon icon="ph:download-simple" fontSize={16} />}
            onClick={() => handleDownloadTemplate(rule.entity)}
          >
            {rule.entity}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {rule.description}
          </Typography>
        </Stack>
      ))}
      <Typography variant="caption" color="text.secondary">
        Required columns: {BULK_UPDATE_REQUIRED_COLUMNS.join(', ')}.
      </Typography>
      {renderValueNote()}
    </Stack>
  );

  const renderPreview = () => (
    <Alert severity="success">
      {file?.name} passed validation ({rows.length} row(s)) and is ready to be uploaded.
    </Alert>
  );

  const renderResult = () => (
    <Alert severity="success">
      <AlertTitle>Upload complete</AlertTitle>
      {file?.name} was uploaded successfully and is being processed.
      {result?.data ? ` ${result.data.updateRowsCount} row(s) were sent for processing.` : ''}
    </Alert>
  );

  const renderBody = () => {
    if (status === 'done') {
      return renderResult();
    }
    return (
      <Stack spacing={2}>
        {renderTemplates()}
        <Divider />
        <BulkUpdateDropzone file={file} disabled={isBusy} onFileSelected={selectFile} />
        {status === 'submitting' ? (
          <Stack spacing={1}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">
              Uploading {file?.name}. Large files can take a while to process.
            </Typography>
          </Stack>
        ) : null}
        {fatalError ? <Alert severity="error">{fatalError}</Alert> : null}
        {errors.length > 0 ? (
          <Stack spacing={1}>
            <Alert severity="error">{errors.length} validation error(s) found. Please fix them and upload again.</Alert>
            <BulkUpdateErrorTable errors={errors} />
          </Stack>
        ) : null}
        {status === 'ready' ? renderPreview() : null}
      </Stack>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => handleClose(reason)}
      maxWidth="md"
      fullWidth
      aria-labelledby="bulk-update-title"
    >
      <DialogTitle id="bulk-update-title">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Bulk Update</Typography>
          <IconButton size="small" onClick={() => handleClose()} disabled={isBusy} aria-label="Close">
            <Icon icon="codicon:chrome-close" fontSize={14} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>{renderBody()}</DialogContent>
      <DialogActions>
        {status !== 'submitting' ? (
          <Button variant="outlined" onClick={() => handleClose()} disabled={status === 'validating'}>
            {status === 'done' ? 'Close' : 'Cancel'}
          </Button>
        ) : null}
        {status !== 'done' ? (
          <Button variant="contained" onClick={handleSubmit} disabled={status !== 'ready'}>
            Upload File
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUpdateModal;
